import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Package, Plus, Trash2, Edit, Loader2, Search, Percent, DollarSign, Tag, ArrowUpDown, X } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('id-ID').format(n || 0);

export default function Bundles() {
  const [bundles, setBundles] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');

  const [form, setForm] = useState({
    name: '', description: '', images: [],
    items: [],
    pricing_type: 'fixed_price',
    discount_value: 0, bundle_price: '',
    stock_mode: 'follow_product', stock: '',
    display_as_upsell: false, trigger_product_ids: [],
    start_date: '', end_date: '', is_active: true
  });

  const fetchData = async () => {
    try {
      const [bRes, pRes] = await Promise.all([
        api.get('/tenant/bundles'),
        api.get('/tenant/products?limit=500')
      ]);
      setBundles(bRes.data.bundles || []);
      setProducts(pRes.data.products || []);
    } catch { toast.error('Gagal memuat data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const resetForm = () => {
    setForm({
      name: '', description: '', images: [],
      items: [], pricing_type: 'fixed_price',
      discount_value: 0, bundle_price: '', stock_mode: 'follow_product', stock: '',
      display_as_upsell: false, trigger_product_ids: [],
      start_date: '', end_date: '', is_active: true
    });
    setEditingId(null);
    setShowDialog(false);
    setProductSearch('');
  };

  const openEdit = async (b) => {
    try {
      const res = await api.get(`/tenant/bundles/${b.id}`);
      const data = res.data;
      setForm({
        name: data.name || '', description: data.description || '',
        images: data.images || [],
        items: (data.items || []).map(i => ({ product_id: i.product_id, quantity: i.quantity || 1, variant_id: i.variant_id || null })),
        pricing_type: data.pricing_type || 'fixed_price',
        discount_value: data.discount_value || 0,
        bundle_price: data.bundle_price || '',
        stock_mode: data.stock_mode || 'follow_product',
        stock: data.stock || '',
        display_as_upsell: data.display_as_upsell || false,
        trigger_product_ids: data.trigger_product_ids || [],
        start_date: data.start_date ? data.start_date.slice(0, 16) : '',
        end_date: data.end_date ? data.end_date.slice(0, 16) : '',
        is_active: data.is_active !== false,
      });
      setEditingId(b.id);
      setShowDialog(true);
    } catch { toast.error('Gagal memuat bundle'); }
  };

  const calcOriginalTotal = useMemo(() => {
    let total = 0;
    for (const it of form.items) {
      const p = products.find(x => x.id === it.product_id);
      if (p) total += (p.sale_price || p.price) * (it.quantity || 1);
    }
    return total;
  }, [form.items, products]);

  const calcFinalPrice = useMemo(() => {
    if (form.pricing_type === 'fixed_price') return parseInt(form.bundle_price) || calcOriginalTotal;
    if (form.pricing_type === 'percentage_discount') return Math.round(calcOriginalTotal * (1 - (form.discount_value || 0) / 100));
    if (form.pricing_type === 'fixed_discount') return Math.max(0, calcOriginalTotal - (parseInt(form.discount_value) || 0));
    return calcOriginalTotal;
  }, [form.pricing_type, form.bundle_price, form.discount_value, calcOriginalTotal]);

  const savingsPercent = calcOriginalTotal > 0
    ? Math.round((1 - calcFinalPrice / calcOriginalTotal) * 100) : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nama bundle wajib diisi'); return; }
    if (form.items.length < 1) { toast.error('Tambahkan minimal 1 produk'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        bundle_price: form.pricing_type === 'fixed_price' ? parseInt(form.bundle_price) || null : null,
        discount_value: parseFloat(form.discount_value) || 0,
        stock: form.stock_mode === 'independent' ? parseInt(form.stock) || 0 : null,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      };
      if (editingId) {
        await api.put(`/tenant/bundles/${editingId}`, payload);
        toast.success('Bundle diperbarui');
      } else {
        await api.post('/tenant/bundles', payload);
        toast.success('Bundle dibuat');
      }
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus bundle ini?')) return;
    try {
      await api.delete(`/tenant/bundles/${id}`);
      toast.success('Bundle dihapus');
      fetchData();
    } catch { toast.error('Gagal menghapus'); }
  };

  const addProduct = (productId) => {
    if (form.items.find(i => i.product_id === productId)) {
      toast.info('Produk sudah ada di bundle');
      return;
    }
    setForm(f => ({ ...f, items: [...f.items, { product_id: productId, quantity: 1, variant_id: null }] }));
    setProductSearch('');
  };

  const removeProduct = (idx) => {
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const updateItemQty = (idx, qty) => {
    setForm(f => ({
      ...f, items: f.items.map((it, i) => i === idx ? { ...it, quantity: Math.max(1, parseInt(qty) || 1) } : it)
    }));
  };

  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return [];
    const q = productSearch.toLowerCase();
    return products.filter(p => p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q)).slice(0, 10);
  }, [productSearch, products]);

  const filteredBundles = bundles.filter(b =>
    !search || b.name?.toLowerCase().includes(search.toLowerCase())
  );

  const getProductName = (pid) => products.find(p => p.id === pid)?.name || pid;
  const getProductPrice = (pid) => {
    const p = products.find(x => x.id === pid);
    return p ? (p.sale_price || p.price) : 0;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Package className="w-6 h-6" /> Product Bundling</h1>
          <p className="text-muted-foreground">Buat paket produk dengan harga spesial</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }}><Plus className="w-4 h-4 mr-2" /> Buat Bundle</Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
        <Input placeholder="Cari bundle..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
      </div>

      {filteredBundles.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Belum ada bundle. Klik "Buat Bundle" untuk mulai.</CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {filteredBundles.map(b => (
            <Card key={b.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg">{b.name}</h3>
                      <Badge variant={b.is_active ? 'default' : 'secondary'}>{b.is_active ? 'Aktif' : 'Nonaktif'}</Badge>
                      {b.display_as_upsell && <Badge variant="outline" className="text-orange-600 border-orange-300">Upsell</Badge>}
                    </div>
                    {b.description && <p className="text-sm text-muted-foreground mb-2">{b.description}</p>}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(b.items || []).map((it, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {getProductName(it.product_id)} ×{it.quantity}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      {b.original_total > b.bundle_price && (
                        <span className="line-through text-muted-foreground">Rp {fmt(b.original_total)}</span>
                      )}
                      <span className="font-bold text-lg text-green-600">Rp {fmt(b.bundle_price)}</span>
                      {b.original_total > b.bundle_price && (
                        <Badge className="bg-red-100 text-red-700">
                          Hemat {Math.round((1 - b.bundle_price / b.original_total) * 100)}%
                        </Badge>
                      )}
                      <span className="text-muted-foreground">
                        {b.pricing_type === 'fixed_price' ? 'Harga tetap' : b.pricing_type === 'percentage_discount' ? `Diskon ${b.discount_value}%` : `Diskon Rp ${fmt(b.discount_value)}`}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button variant="outline" size="sm" onClick={() => openEdit(b)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(b.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={v => { if (!v) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Bundle' : 'Buat Bundle Baru'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Basic Info */}
            <div className="space-y-3">
              <div>
                <Label>Nama Bundle *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Paket Hemat A" />
              </div>
              <div>
                <Label>Deskripsi</Label>
                <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Deskripsi bundle..." rows={2} />
              </div>
            </div>

            {/* Product Picker */}
            <div className="space-y-3">
              <Label className="font-semibold">Produk dalam Bundle</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                <Input placeholder="Cari produk untuk ditambahkan..." value={productSearch} onChange={e => setProductSearch(e.target.value)} className="pl-9" />
                {filteredProducts.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredProducts.map(p => (
                      <div key={p.id} className="px-3 py-2 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => addProduct(p.id)}>
                        <span className="text-sm">{p.name}</span>
                        <span className="text-xs text-muted-foreground">Rp {fmt(p.sale_price || p.price)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {form.items.length > 0 && (
                <div className="border rounded-md divide-y">
                  {form.items.map((it, idx) => {
                    const p = products.find(x => x.id === it.product_id);
                    return (
                      <div key={idx} className="flex items-center gap-3 p-3">
                        {p?.images?.[0] && <img src={p.images[0]} alt="" className="w-10 h-10 rounded object-cover" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{p?.name || it.product_id}</div>
                          <div className="text-xs text-muted-foreground">Rp {fmt(p?.sale_price || p?.price)} / pcs</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs">Qty:</Label>
                          <Input type="number" min={1} value={it.quantity} onChange={e => updateItemQty(idx, e.target.value)} className="w-16 h-8 text-center" />
                        </div>
                        <div className="text-sm font-medium w-28 text-right">
                          Rp {fmt((p?.sale_price || p?.price || 0) * it.quantity)}
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeProduct(idx)}>
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    );
                  })}
                  <div className="p-3 flex justify-between text-sm font-medium bg-gray-50">
                    <span>Total Harga Normal ({form.items.reduce((a, b) => a + b.quantity, 0)} item)</span>
                    <span>Rp {fmt(calcOriginalTotal)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Pricing */}
            <div className="space-y-3">
              <Label className="font-semibold">Harga Bundle</Label>
              <div className="flex gap-2">
                {[
                  { val: 'fixed_price', label: 'Harga Tetap', icon: DollarSign },
                  { val: 'percentage_discount', label: 'Diskon %', icon: Percent },
                  { val: 'fixed_discount', label: 'Diskon Rp', icon: Tag },
                ].map(t => (
                  <Button key={t.val} type="button" variant={form.pricing_type === t.val ? 'default' : 'outline'} size="sm"
                    onClick={() => setForm(f => ({ ...f, pricing_type: t.val }))}>
                    <t.icon className="w-3 h-3 mr-1" /> {t.label}
                  </Button>
                ))}
              </div>
              {form.pricing_type === 'fixed_price' && (
                <div>
                  <Label>Harga Bundle (Rp)</Label>
                  <Input type="number" value={form.bundle_price} onChange={e => setForm(f => ({ ...f, bundle_price: e.target.value }))} placeholder={String(calcOriginalTotal)} />
                </div>
              )}
              {form.pricing_type === 'percentage_discount' && (
                <div>
                  <Label>Diskon (%)</Label>
                  <Input type="number" min={0} max={100} value={form.discount_value} onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))} placeholder="10" />
                </div>
              )}
              {form.pricing_type === 'fixed_discount' && (
                <div>
                  <Label>Potongan (Rp)</Label>
                  <Input type="number" value={form.discount_value} onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))} placeholder="50000" />
                </div>
              )}

              {form.items.length > 0 && (
                <div className="p-3 bg-green-50 rounded-md flex items-center justify-between">
                  <span>Harga Bundle Final</span>
                  <div className="text-right">
                    {calcOriginalTotal > calcFinalPrice && (
                      <span className="text-sm line-through text-muted-foreground mr-2">Rp {fmt(calcOriginalTotal)}</span>
                    )}
                    <span className="text-lg font-bold text-green-700">Rp {fmt(calcFinalPrice)}</span>
                    {savingsPercent > 0 && <Badge className="ml-2 bg-red-100 text-red-700">Hemat {savingsPercent}%</Badge>}
                  </div>
                </div>
              )}
            </div>

            {/* Upsell Settings */}
            <div className="space-y-3">
              <Label className="font-semibold">Upsell</Label>
              <div className="flex items-center gap-3">
                <Switch checked={form.display_as_upsell} onCheckedChange={v => setForm(f => ({ ...f, display_as_upsell: v }))} />
                <span className="text-sm">Tampilkan sebagai upsell di halaman produk & checkout</span>
              </div>
              {form.display_as_upsell && (
                <div>
                  <Label className="text-xs text-muted-foreground">Trigger produk (opsional — kosong = selalu tampil jika ada item bundle di cart)</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {form.trigger_product_ids.map((pid, idx) => (
                      <Badge key={idx} variant="outline" className="gap-1">
                        {getProductName(pid)}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => setForm(f => ({ ...f, trigger_product_ids: f.trigger_product_ids.filter((_, i) => i !== idx) }))} />
                      </Badge>
                    ))}
                  </div>
                  <select className="w-full mt-1 border rounded px-2 py-1 text-sm"
                    value="" onChange={e => {
                      if (e.target.value && !form.trigger_product_ids.includes(e.target.value))
                        setForm(f => ({ ...f, trigger_product_ids: [...f.trigger_product_ids, e.target.value] }));
                    }}>
                    <option value="">+ Tambah trigger produk...</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
            </div>

            {/* Stock Mode */}
            <div className="space-y-3">
              <Label className="font-semibold">Stok</Label>
              <div className="flex gap-2">
                <Button type="button" variant={form.stock_mode === 'follow_product' ? 'default' : 'outline'} size="sm"
                  onClick={() => setForm(f => ({ ...f, stock_mode: 'follow_product' }))}>
                  <ArrowUpDown className="w-3 h-3 mr-1" /> Ikuti Stok Produk
                </Button>
                <Button type="button" variant={form.stock_mode === 'independent' ? 'default' : 'outline'} size="sm"
                  onClick={() => setForm(f => ({ ...f, stock_mode: 'independent' }))}>
                  Stok Mandiri
                </Button>
              </div>
              {form.stock_mode === 'independent' && (
                <div>
                  <Label>Jumlah Stok Bundle</Label>
                  <Input type="number" min={0} value={form.stock} onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} />
                </div>
              )}
            </div>

            {/* Schedule */}
            <div className="space-y-3">
              <Label className="font-semibold">Jadwal (Opsional)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Mulai</Label>
                  <Input type="datetime-local" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Berakhir</Label>
                  <Input type="datetime-local" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
                </div>
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} />
              <Label>Aktif</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={resetForm}>Batal</Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingId ? 'Simpan' : 'Buat Bundle'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
